import type {Translator} from '@yandex-delivery/core/i18n'
import {TakeFirstSchema} from '@yandex-delivery/form'
import {isValidEmail} from '@yandex-delivery/utils'
import {appConfig, i18n} from 'src/shared/config'
import {DeliveryType} from 'src/shared/types'
import {OfferDeliveryType} from 'src/widgets/desktop'
import {OrderParcelSize} from 'src/widgets/Parcel/consts'
import {array, object, string} from 'yup'

import {isEmptyOrUndefined} from '../lib/is-empty-or-undefined'

const getDeliveryTypeSchema = (t: Translator) =>
  string()
    .oneOf([DeliveryType.NDD, DeliveryType.SDD, DeliveryType.Express])
    .required(t('validation.required'))

const getPickUpDropOffPointSchema = () =>
  object().shape({
    city: object()
      .optional()
      .required(i18n('validation.required', 'Обязательное поле')),
    point: object()
      .optional()
      .required(i18n('validation.required', 'Обязательное поле')),
  })

const NddContactSchema = object().shape({
  phone: string().test(
    'phone',
    i18n('validation.phone.invalid', 'Неверный формат телефона'),
    appConfig.checkIsValidPhone,
  ),
  name: string().required(i18n('validation.required', 'Обязательное поле')),
})

export function getEmailInputSchema(t: Translator) {
  return string()
    .email(t('claim.form.validation.email'))
    .test(
      'is-valid',
      t('claim.form.validation.email'),
      function testEmailInput(value = '') {
        return isEmptyOrUndefined(value) || isValidEmail(value)
      },
    )
}

const ContactSchema = object().shape({
  phone: string()
    .required(i18n('validation.required', 'Обязательное поле'))
    .test(
      'phone',
      i18n('validation.phone.invalid', 'Неверный формат телефона'),
      appConfig.checkIsValidPhone,
    ),
  email: getEmailInputSchema(i18n),
})

const AddressSchema = object().shape({
  fullName: string().required(i18n('validation.required', 'Обязательное поле')),
})

const getProductSchema = () =>
  object().shape({
    name: string().required(i18n('validation.required', 'Обязательное поле')),
  })

const getPaymentMethodSchema = (t: Translator) =>
  object().shape({
    id: string().required(t('validation.required')),
  })

const getVerticalSchema = (t: Translator) =>
  object().shape({
    name: string().required(t('claim.form.validation.required')),
  })

const getParcelSizeSchema = () =>
  string()
    .oneOf([
      OrderParcelSize.ExtraSmall,
      OrderParcelSize.Small,
      OrderParcelSize.Medium,
      OrderParcelSize.Large,
      OrderParcelSize.ExtraLarge,
    ])
    .required(i18n('validation.required', 'Обязательное поле'))

const getOfferSchema = (t: Translator) =>
  object().shape({
    id: string().required(t('claim.form.validation.required')),
  })

const getOfferFlowDeliveryTypeSchema = () =>
  string()
    .optional()
    .default(undefined)
    .notRequired()
    .oneOf([OfferDeliveryType.Now, OfferDeliveryType.ToTime])

const NddDestinationPointSchema = object().shape({
  pickUpPoint: getPickUpDropOffPointSchema(),
  contact: NddContactSchema,
  parcels: array()
    .min(1)
    .of(
      object().shape({
        size: getParcelSizeSchema(),
        products: array().min(1).of(getProductSchema()),
      }),
    ),
})

/**
 * Схема валидации формы заказа
 * Не уверен, что сейчас уместно разделять
 * эту схему на маленькие блоки так как предстоит рефакторинг этой формы
 */
function getFormValidationSchema(t: Translator) {
  return object({
    deliveryType: getDeliveryTypeSchema(t),
    sourcePoint: object().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema =>
        schema.shape({
          dropOffPoint: getPickUpDropOffPointSchema(),
          contact: object().shape({
            phone: string()
              .optional()
              .notRequired()
              .test(
                'phone',
                i18n('validation.phone.invalid', 'Неверный формат телефона'),
                value => {
                  if (value) {
                    return appConfig.checkIsValidPhone(value)
                  }

                  return true
                },
              ),
          }),
        }),
      otherwise: schema =>
        schema.shape({
          address: AddressSchema,
          contact: ContactSchema,
        }),
    }),
    destinationPoints: array().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema =>
        schema
          .min(1)
          .concat(TakeFirstSchema) // в ndd возможен только один destination, поэтому берем только первый
          .of(NddDestinationPointSchema),
      otherwise: schema =>
        schema.min(1).of(
          object().shape({
            address: AddressSchema,
            contact: ContactSchema,
            parcels: array()
              .min(1)
              .of(
                object().shape({
                  products: array().min(1).of(getProductSchema()),
                }),
              ),
          }),
        ),
    }),
    ndd: object().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema =>
        schema.shape({
          deliveryDate: string().required(t('claim.form.validation.required')),
        }),
      otherwise: schema => schema.optional(),
    }),
    nddOrder: object().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema =>
        schema.shape({
          id: string().required(t('claim.form.validation.required')),
          price: string().required(t('claim.form.validation.required')),
          totalPrice: string().required(t('claim.form.validation.required')),
        }),
      otherwise: schema => schema.optional(),
    }),
    paymentMethod: getPaymentMethodSchema(t),
    offerFlow: object().when('deliveryType', {
      is: (value: DeliveryType) => value === DeliveryType.NDD,
      then: schema => schema.optional(),
      otherwise: schema =>
        schema.shape({
          deliveryType: getOfferFlowDeliveryTypeSchema(),
          offer: getOfferSchema(t),
          deliveryInterval: object()
            .default(undefined)
            .when('deliveryType', {
              is: (deliveryType?: OfferDeliveryType) =>
                deliveryType === OfferDeliveryType.ToTime,
              then: formSchema =>
                formSchema
                  .shape({
                    from: string().required(
                      t('claim.form.validation.required'),
                    ),
                    to: string().required(t('claim.form.validation.required')),
                  })
                  .required(t('claim.form.validation.required')),
              otherwise: formSchema =>
                formSchema.test(
                  'deliveryInterval',
                  t('claim.form.validation.required'),
                  function validateDeliveryInterval(value, context) {
                    // TODO фиксить здесь https://st.yandex-team.ru/CARGODEV-23436
                    // Это грязный хак, который подглядел здесь https://github.com/jquense/yup/issues/735
                    // when не работает со свойством, которое находится выше по дереву, от этого и идет невозможность привязки к offerFlow.vertical.name
                    // @ts-expect-error - from существует в context
                    const isNightVertical =
                      context.from[2]?.value?.offerFlow?.vertical?.name ===
                      DeliveryType.Night

                    return isNightVertical ? !!value : true
                  },
                ),
            }),
          vertical: getVerticalSchema(t),
        }),
    }),
  })
}

export const FormSchema = getFormValidationSchema(i18n)
